import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  ContactInfoInline,
  SummarySection,
  CoreCompetenciesSection,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  PaperSection,
  SkillSection,
  getPlaceholderClass,
  renderBulletPoints
} from '../common/ResumeComponents';

// Template 9 - 모던한 옐로우 액센트 디자인
const Template9 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`template-9 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      <div>
        {/* Header with yellow background */}
        <div className="bg-yellow-400 px-8 py-8 mb-6">
          <h1 className={getPlaceholderClass(hasRealData.name, "text-3xl font-bold mb-1 text-gray-900", "text-3xl font-bold mb-1 text-gray-600 italic")}>
            {currentData.name}
          </h1>
          <p className={getPlaceholderClass(hasRealData.title, "text-base font-medium mb-3 text-gray-800", "text-base font-medium mb-3 text-gray-600 italic")}>
            {currentData.title}
          </p>
          
          {/* Contact Info */}
          <ContactInfoInline
            phone={currentData.phone}
            email={currentData.email}
            address={currentData.address}
            socialLinks={currentData.socialLinks}
            hasRealData={hasRealData}
            separator=" | "
            showIcons={true}
            textColor="text-gray-800"
          />
        </div>
        
        <div className="px-8">{/* Content wrapper */}

        {/* Summary */}
        {currentData.summary && (
          <section className="mb-6">
            <div className="border-l-4 border-yellow-400 pl-4 mb-4">
              <h2 className="text-base font-semibold uppercase text-gray-800">
                {titles.introduction}
              </h2>
            </div>
            <SummarySection
              summary={currentData.summary}
              hasRealData={hasRealData}
              textClassName="text-sm leading-relaxed text-gray-700"
            />
          </section>
        )}

        {/* Core Competencies */}
        {currentData.keyStrengths.length > 0 && (
          <section className="mb-6">
            <div className="border-l-4 border-yellow-400 pl-4 mb-4">
              <h2 className="text-base font-semibold uppercase text-gray-800">
                {titles.coreCompetencies}
              </h2>
            </div>
            <CoreCompetenciesSection
              keyStrengths={currentData.keyStrengths}
              hasRealData={hasRealData}
              className="space-y-1"
              itemClassName="text-sm"
              bulletStyle="•"
              bulletClassName="text-gray-700"
              textClassName="text-gray-700"
            />
          </section>
        )}

        {/* Professional Experience */}
        <section className="mb-6">
          <div className="border-l-4 border-yellow-400 pl-4 mb-4">
            <h2 className="text-base font-semibold uppercase text-gray-800">
              {titles.experience}
            </h2>
          </div>
          {currentData.experiences.map((exp, index) => (
            <div key={index} className="mb-5 last:mb-0">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className={getPlaceholderClass(hasRealData.experiences, "font-bold text-gray-800")}>
                    {exp.position}
                  </h3>
                  <p className={getPlaceholderClass(hasRealData.experiences, "text-sm text-gray-700")}>
                    {exp.company}
                  </p>
                </div>
                <div className={getPlaceholderClass(hasRealData.experiences, "text-sm text-right text-gray-600")}>
                  {exp.startDate} - {exp.endDate}
                </div>
              </div>
              
              {exp.overallDescription && (
                <div className="text-sm mt-2">
                  {renderBulletPoints(exp.overallDescription, "•", "pl-0", "mb-1", !hasRealData.experiences)}
                </div>
              )}

              {exp.projects && exp.projects.length > 0 && (
                <div className="mt-3">
                  {exp.projects.map((project, projIndex) => (
                    <div key={projIndex} className="mb-3 last:mb-0">
                      <h4 className={getPlaceholderClass(hasRealData.experiences, "text-sm font-semibold text-gray-800 mb-1")}>
                        <span className="bg-yellow-400 px-2 py-0.5">
                          {project.projectName}
                        </span>
                      </h4>
                      {project.projectDescription && (
                        <div className="text-sm">
                          {renderBulletPoints(project.projectDescription, "•", "pl-4", "mb-0.5", !hasRealData.experiences)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </section>


        {/* Two Column Section for Education and Certificates */}
        <div className="grid grid-cols-2 gap-8 mb-6">
          {/* Education */}
          <section>
            <div className="border-l-4 border-yellow-400 pl-4 mb-4">
              <h2 className="text-base font-semibold uppercase text-gray-800">
                {titles.education}
              </h2>
            </div>
            <EducationSection
              education={currentData.education}
              hasRealData={hasRealData}
              itemClassName="mb-4 last:mb-0"
              datePosition="right"
              degreeClassName="font-bold text-gray-800"
              schoolClassName="text-sm text-gray-700"
              majorClassName="text-sm text-gray-600"
              dateClassName="text-sm text-gray-600"
              showMajor={true}
            />
          </section>

          {/* Certificates */}
          {currentData.certificates.length > 0 && (
            <section>
              <div className="border-l-4 border-yellow-400 pl-4 mb-4">
                <h2 className="text-base font-semibold uppercase text-gray-800">
                  {titles.certificates}
                </h2>
              </div>
              <CertificateSection
                certificates={currentData.certificates}
                hasRealData={hasRealData}
                itemClassName="mb-3 last:mb-0"
                nameClassName="text-sm font-semibold text-gray-800"
                issuerClassName="text-sm text-gray-600"
                dateClassName="text-sm text-gray-600"
                dateFormat="separate"
              />
            </section>
          )}
        </div>

        {/* Two Column Section for Papers and Skills */}
        <div className="grid grid-cols-2 gap-8 mb-6">
          {/* Papers/Publications */}
          {hasRealData.papers && (
            <section>
              <div className="border-l-4 border-yellow-400 pl-4 mb-4">
                <h2 className="text-base font-semibold uppercase text-gray-800">
                  {titles.papers}
                </h2>
              </div>
              <PaperSection
                papers={currentData.papers}
                hasRealData={hasRealData}
                getPaperTypeText={getPaperTypeText}
                itemClassName="mb-4 last:mb-0"
                titleClassName="text-sm font-semibold text-gray-800"
                authorsClassName="text-sm text-gray-600"
                journalClassName="text-sm text-gray-600"
                typeClassName="ml-2 px-2 py-0.5 bg-yellow-50 border border-yellow-300 text-gray-700 text-xs rounded"
              />
            </section>
          )}

          {/* Skills */}
          {currentData.skills.length > 0 && (
            <section>
              <div className="border-l-4 border-yellow-400 pl-4 mb-4">
                <h2 className="text-base font-semibold uppercase text-gray-800">
                  {titles.skills}
                </h2>
              </div>
              <SkillSection
                skills={currentData.skills}
                hasRealData={hasRealData}
                variant="inline"
                className="text-sm text-gray-700"
              />
            </section>
          )}
        </div>
        </div>{/* End of content wrapper */}
      </div>
    </div>
  );
};

export default Template9;